import { ActivatedRoute } from '@angular/router';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { map } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { NumeronCollectionModel } from '../models/numeron.model';
import { NumeronsService } from '../services/numerons.service';
import { Ga4AnalyticsService } from '../../../core/analytics/ga4-analytics.service';
import { InfiniteScrollSentinelDirective } from '../../../shared/directives/infinite-scroll-sentinel.directive';
import { OrderedMasonryDirective } from '../../../shared/directives/ordered-masonry.directive';
import { OrderedMasonryItemDirective } from '../../../shared/directives/ordered-masonry-item.directive';
import { CollectionFeedCardComponent } from '../../../shared/ui/collection-feed-card/collection-feed-card.component';
import { NumeronCardComponent } from '../../../shared/ui/numeron-card/numeron-card.component';

@Component({
  selector: 'app-by-publisher-page',
  imports: [
    InfiniteScrollSentinelDirective,
    OrderedMasonryDirective,
    OrderedMasonryItemDirective,
    CollectionFeedCardComponent,
    NumeronCardComponent,
  ],
  templateUrl: './by-publisher-page.component.html',
})
export class ByPublisherPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly numeronsService = inject(NumeronsService);
  private readonly ga4AnalyticsService = inject(Ga4AnalyticsService);
  private readonly title = inject(Title);
  private readonly destroyRef = inject(DestroyRef);

  private readonly pageSize = 10;
  private offset = 0;
  private publisherId = '';
  private lastTrackedTitle = '';

  protected readonly publisherTitle = signal('Publisher');
  protected readonly collections = signal<NumeronCollectionModel[]>([]);
  protected readonly isLoading = signal(false);
  protected readonly isLoadingMore = signal(false);
  protected readonly hasError = signal(false);
  protected readonly hasMore = signal(true);

  public constructor() {
    this.route.paramMap
      .pipe(
        map((params) => params.get('publisherId') ?? ''),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((publisherId) => {
        this.publisherId = publisherId;
        this.resetAndLoad();
      });
  }

  protected trackByCollectionId(_: number, collection: NumeronCollectionModel): string {
    return collection._id;
  }

  protected loadMore(): void {
    this.fetchByPublisher(false);
  }

  private resetAndLoad(): void {
    this.offset = 0;
    this.publisherTitle.set('Publisher');
    this.lastTrackedTitle = '';
    this.collections.set([]);
    this.hasError.set(false);
    this.hasMore.set(true);
    this.isLoading.set(false);
    this.isLoadingMore.set(false);

    this.fetchByPublisher(true);
  }

  private fetchByPublisher(isInitialLoad: boolean): void {
    if (!this.publisherId || !this.hasMore() || this.isLoading() || this.isLoadingMore()) {
      return;
    }

    this.hasError.set(false);

    if (isInitialLoad) {
      this.isLoading.set(true);
    } else {
      this.isLoadingMore.set(true);
    }

    this.numeronsService
      .getByPublisherNumeronCollections(this.publisherId, this.offset, this.pageSize)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (incomingCollections) => {
          this.collections.update((existingCollections) =>
            isInitialLoad ? incomingCollections : [...existingCollections, ...incomingCollections],
          );
          this.offset += incomingCollections.length;
          this.hasMore.set(incomingCollections.length === this.pageSize);
          const resolvedPublisherTitle =
            this.extractPublisherTitle(incomingCollections) ?? this.publisherTitle();
          this.publisherTitle.set(resolvedPublisherTitle);
          this.updatePageTitleAndTrack(resolvedPublisherTitle);
          this.isLoading.set(false);
          this.isLoadingMore.set(false);
        },
        error: () => {
          this.hasError.set(true);
          this.isLoading.set(false);
          this.isLoadingMore.set(false);
        },
      });
  }

  private extractPublisherTitle(collections: NumeronCollectionModel[]): string | null {
    for (const collection of collections) {
      const collectionPublisher = collection.user?.displayName?.trim();
      if (collectionPublisher) {
        return collectionPublisher;
      }

      const numeronPublisher = collection.numerons[0]?.user?.displayName?.trim();
      if (numeronPublisher) {
        return numeronPublisher;
      }
    }

    return null;
  }

  private updatePageTitleAndTrack(publisherTitle: string): void {
    const pageTitle = `By Publisher - ${publisherTitle}`;
    if (pageTitle === this.lastTrackedTitle) {
      return;
    }

    this.lastTrackedTitle = pageTitle;
    this.title.setTitle(pageTitle);
    this.ga4AnalyticsService.trackPageView({
      contentGroup: `Publisher - ${publisherTitle}`,
      contentId: this.publisherId,
      contentType: 'Collections',
    });
  }
}
